import React, { Suspense } from 'react';
import type { AtlassianIconType, RenderFn } from '@atlassian/forge-ui-types';
import { AtlassianIconComponents } from './__generated__';
import warnOnce from '../../../utils/warn-once';


export type AtlassianIconProps = {
	/**
	 * The glyph type of AtlassianIcon to render. This will automatically set the color.
	 * Available options are derived from @atlaskit/object metadata and will
	 * automatically include any new object types added to the design system.
	 */
	glyph: AtlassianIconType;
	/**
	 * The label for the AtlassianIcon. If decorative, set to empty string.
	 */
	label?: string;
	/**
	 * The size of the AtlassianIcon: 'small' (12px) or 'medium' (16px).
	 */
	size?: 'small' | 'medium';
	/**
	 * Test ID for testing
	 */
	testId?: string;
};

/**
 * An AtlassianIcon is an icon that represents an Atlassian-specific content type.
 * The glyph prop automatically sets the color for the object.
 */
export const AtlassianIcon = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { glyph, label, size, testId } = props.forgeDoc.props as AtlassianIconProps;

	const Component = AtlassianIconComponents[glyph];

	if (!Component) {
		warnOnce(`[@forge/react: Object] no object found for glyph "${glyph}".`);
		return <></>;
	}

	return (
		<Suspense fallback={null}>
			<Component testId={testId} label={label} size={size} />
		</Suspense>

	);
};

